'use client'
import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useSession, signOut } from 'next-auth/react';
import Link from 'next/link';
import Image from 'next/image';
import LocalizedName from '@/components/LocalizedName';
import { useProfileCache, useScrollRestoration } from '@/lib/hooks/useProfileCache';
import { FiArrowDown, FiArrowUp } from 'react-icons/fi';

const PROFILES_CACHE_KEY = 'profilesData';
const PROFILES_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const PROFILES_FILTERS_KEY = 'profilesFilters';
const SEARCH_DEBOUNCE_DELAY = 500; // 500ms debounce
const MIN_SEARCH_LENGTH = 3; // Minimum 3 characters to search

export default function ProfilesPage() {
    const router = useRouter();
    const { data: session, status } = useSession();
    const { getCachedProfile, setCachedProfile } = useProfileCache();

    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters with persistence
    const [selectedDivision, setSelectedDivision] = useState('');
    const [selectedRole, setSelectedRole] = useState('');
    const [showNRI, setShowNRI] = useState(false);
    const [selectedLetter, setSelectedLetter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [activeSearchQuery, setActiveSearchQuery] = useState(''); // The query that was actually searched
    const [sortOrder, setSortOrder] = useState('alphabetical'); // 'alphabetical' or 'latest'

    // Refs
    const mainContainerRef = useRef(null);
    const cacheTimerRef = useRef(null);
    const searchTimeoutRef = useRef(null);

    // Scroll restoration hook
    useScrollRestoration(mainContainerRef);

    // Debounce search query
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, SEARCH_DEBOUNCE_DELAY);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    // Handle search execution
    const executeSearch = () => {
        const trimmedQuery = searchQuery.trim();

        if (trimmedQuery && trimmedQuery.length >= MIN_SEARCH_LENGTH) {
            setSearchLoading(true);
            setSearchError('');
            setActiveSearchQuery(trimmedQuery);
            fetchUsers(false, trimmedQuery).finally(() => {
                setSearchLoading(false);
            });
        } else if (trimmedQuery && trimmedQuery.length < MIN_SEARCH_LENGTH) {
            setSearchError(`Please enter at least ${MIN_SEARCH_LENGTH} characters to search`);
            setActiveSearchQuery('');
            fetchUsers(); // Show all users
        } else {
            // Empty search - show all users
            setSearchError('');
            setActiveSearchQuery('');
            fetchUsers();
        }
    };

    // Handle Enter key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            executeSearch();
        }
    };

    // Save scroll position on component unmount
    useEffect(() => {
        return () => {
            // Don't save scroll position if we're navigating to a profile (already saved in click handler)
            const isNavigatingToProfile = sessionStorage.getItem('navigatingToProfile') === 'true';
            // Also don't overwrite if we already have a valid scroll position saved
            const currentSaved = sessionStorage.getItem('profilesScrollPosition');
            const hasValidPosition = currentSaved && parseInt(currentSaved, 10) > 0;

            if (!isNavigatingToProfile && !hasValidPosition) {
                try {
                    const scrollPos = window.scrollY.toString();
                    sessionStorage.setItem('profilesScrollPosition', scrollPos);
                } catch (error) {
                    console.error('Error saving scroll position on unmount:', error);
                }
            }
        };
    }, []);

    // Save scroll position before navigation
    useEffect(() => {
        const handleBeforeUnload = () => {
            // Don't save if we're navigating to a profile (already saved in click handler)
            const isNavigatingToProfile = sessionStorage.getItem('navigatingToProfile') === 'true';
            if (!isNavigatingToProfile) {
                try {
                    const scrollPos = window.scrollY.toString();
                    sessionStorage.setItem('profilesScrollPosition', scrollPos);
                } catch (error) {
                    console.error('Error saving scroll position on unload:', error);
                }
            }
        };

        // Save scroll position when the page becomes hidden (navigation away)
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Don't save if we're navigating to a profile (already saved in click handler)
                const isNavigatingToProfile = sessionStorage.getItem('navigatingToProfile') === 'true';
                if (!isNavigatingToProfile) {
                    try {
                        sessionStorage.setItem('profilesScrollPosition', window.scrollY.toString());
                    } catch (error) {
                        console.error('Error saving scroll position on visibility change:', error);
                    }
                }
            }
        };

        // Listen for browser navigation events
        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Cleanup
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // Load filters from session storage
    useEffect(() => {
        try {
            const savedFilters = sessionStorage.getItem(PROFILES_FILTERS_KEY);
            if (savedFilters) {
                const filters = JSON.parse(savedFilters);
                setSelectedDivision(filters.division || '');
                setSelectedRole(filters.role || '');
                setShowNRI(filters.nri || false);
                setSelectedLetter(filters.letter || '');
                setSearchQuery(filters.search || '');
            }
        } catch (error) {
            console.error('Error loading filters:', error);
        }
    }, []);

    // Save filters whenever they change
    useEffect(() => {
        try {
            sessionStorage.setItem(PROFILES_FILTERS_KEY, JSON.stringify({
                division: selectedDivision,
                role: selectedRole,
                nri: showNRI,
                letter: selectedLetter,
                search: searchQuery
            }));
        } catch (error) {
            console.error('Error saving filters:', error);
        }
    }, [selectedDivision, selectedRole, showNRI, selectedLetter, searchQuery]);

    // Fetch users with caching and search
    const fetchUsers = useCallback(async (forceRefresh = false, searchParam = '', sortParam = sortOrder) => {
        try {
            // Try to get cached data first (unless force refresh)
            const cacheKey = `${PROFILES_CACHE_KEY}_${sortParam || 'default'}`;
            if (!forceRefresh && !searchParam) {
                const cached = localStorage.getItem(cacheKey);
                if (cached) {
                    try {
                        const parsed = JSON.parse(cached);
                        if (Date.now() - parsed.timestamp < PROFILES_CACHE_DURATION) {
                            setUsers(parsed.data);
                            setFilteredUsers(parsed.data);
                            setLoading(false);
                            return;
                        }
                    } catch (error) {
                        console.error('Error parsing cached profiles:', error);
                    }
                }
            }

            // Fetch fresh data only if cache is invalid or force refresh
            const params = new URLSearchParams();
            if (searchParam) params.set('search', searchParam);
            if (sortParam) params.set('sort', sortParam);
            const qs = params.toString();
            const response = await fetch(`/api/profiles${qs ? `?${qs}` : ''}`);
            const data = await response.json();

            if (data.success) {
                // Make a copy and optionally sort client-side (case-insensitive)
                let usersData = Array.isArray(data.users) ? data.users.slice() : [];

                if (sortParam === 'alphabetical') {
                    usersData.sort((a, b) => {
                        const nameA = `${(a.name || '').trim()} ${(a.surname || '').trim()}`.trim().toLowerCase();
                        const nameB = `${(b.name || '').trim()} ${(b.surname || '').trim()}`.trim().toLowerCase();
                        return nameA.localeCompare(nameB);
                    });
                }

                setUsers(usersData);
                setFilteredUsers(usersData);

                // Cache the data only if no search parameter (to avoid caching search results)
                if (!searchParam) {
                    try {
                        localStorage.setItem(cacheKey, JSON.stringify({
                            data: usersData,
                            timestamp: Date.now()
                        }));
                        // Reset/refresh cache invalidation timer
                        if (cacheTimerRef.current) {
                            clearTimeout(cacheTimerRef.current);
                        }
                        cacheTimerRef.current = setTimeout(() => {
                            try { localStorage.removeItem(cacheKey); } catch (e) { /* ignore */ }
                        }, PROFILES_CACHE_DURATION);
                    } catch (error) {
                        console.error('Error caching profiles:', error);
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    }, [sortOrder]);

    // Fetch users on mount
    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Apply filters with memoization (preserve server ordering)
    const applyFilters = useCallback(() => {
        let filtered = [...users];

        if (selectedDivision) {
            filtered = filtered.filter(user => user.division === selectedDivision);
        }

        if (selectedRole) {
            filtered = filtered.filter(user => user.role === selectedRole);
        }

        if (showNRI) {
            filtered = filtered.filter(user => user.isNRI === true);
        }

        if (selectedLetter) {
            filtered = filtered.filter(user => {
                const fullName = `${(user.name || '').trim()} ${(user.surname || '').trim()}`.trim();
                return fullName.charAt(0).toUpperCase() === selectedLetter.toUpperCase();
            });
        }

        // Server provides sorting now; just preserve returned order
        setFilteredUsers(filtered);
    }, [users, selectedDivision, selectedRole, showNRI, selectedLetter]);

    // Apply filters when dependencies change
    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    // Refetch when sort order changes to get server-sorted results
    useEffect(() => {
        fetchUsers(true);
    }, [sortOrder]);

    // Restore scroll position after profiles are loaded
    useEffect(() => {
        if (!loading && filteredUsers.length > 0) {
            const comingFromProfile = sessionStorage.getItem('comingFromProfiles') === 'true';
            if (comingFromProfile) {
                try {
                    const savedPosition = sessionStorage.getItem('profilesScrollPosition');
                    if (savedPosition) {
                        const position = parseInt(savedPosition, 10);
                        if (position > 0) {
                            // Set restoring flag to prevent scroll listener from overwriting
                            sessionStorage.setItem('isRestoringScroll', 'true');
                            // Wait 2 seconds after data load to ensure DOM is stable
                            const scrollToPosition = () => {
                                window.scrollTo({
                                    top: position,
                                    behavior: 'instant'
                                });
                                // Clear restoring flag after scroll
                                sessionStorage.removeItem('isRestoringScroll');
                            };
                            setTimeout(scrollToPosition, 2000);
                            // Additional retry after 3 seconds in case images are still loading
                            setTimeout(() => {
                                scrollToPosition();
                            }, 3000);
                        }
                    }
                } catch (error) {
                    console.error('Error restoring scroll position after load:', error);
                    sessionStorage.removeItem('isRestoringScroll');
                } finally {
                    sessionStorage.removeItem('comingFromProfiles');
                    sessionStorage.removeItem('navigatingToProfile'); // Clean up
                }
            }
        }
    }, [loading, filteredUsers.length]);

    const handleLogout = async () => {
        // Clear caches on logout
        try {
            localStorage.removeItem(PROFILES_CACHE_KEY);
            sessionStorage.removeItem(PROFILES_FILTERS_KEY);
            sessionStorage.removeItem('profilesScrollPosition');
        } catch (error) {
            console.error('Error clearing cache on logout:', error);
        }
        await signOut({ redirect: true, callbackUrl: '/' });
    };

    if (status === 'loading' || loading || searchLoading) {
        return (
            <div className="profile_fo_loading">
                <div className="profile_fo_loading-spinner"></div>
                <p>{searchLoading ? 'Searching profiles...' : 'Loading profiles...'}</p>
            </div>
        );
    }

    const currentUser = session?.user;

    return (
        <div className="profile_fo_container" ref={mainContainerRef}>
            {/* Header */}
            <header className="profile_fo_header">
                <div className="profile_fo_header-content">
                    <h1 className="profile_fo_title">
                        All Profiles
                        <span className="profile_fo_subtitle">બધા પ્રોફાઇલ્સ</span>
                    </h1>

                    <div className="profile_fo_user-actions">
                        {currentUser ? (
                            <>
                                <div className="profile_fo_user-info">
                                    <span className="profile_fo_welcome">Welcome,</span>
                                    <span className="profile_fo_user-name"><LocalizedName user={currentUser} initial={currentUser.name} /></span>
                                </div>
                                {currentUser.role === 'master_admin' ? (
                                    <Link href="/admin/dashboard" className="profile_fo_button profile_fo_button-primary">Dashboard</Link>
                                ) : (
                                    currentUser.id && (
                                        <Link href={`/profile/${currentUser.id}`} className="profile_fo_button profile_fo_button-primary">My Profile</Link>
                                    )
                                )}
                                <Link
                                    href="/chat"
                                    className="profile_fo_button profile_fo_button-primary"
                                    style={{ backgroundColor: '#2196F3' }}
                                >
                                    💬 Chat
                                </Link>
                                <Link
                                    href="/nearby-friends"
                                    className="profile_fo_button profile_fo_button-primary"
                                    style={{ backgroundColor: '#FF9800' }}
                                >
                                    Nearby Friends
                                </Link>
                                <button
                                    onClick={handleLogout}
                                    className="profile_fo_button profile_fo_button-secondary"
                                >
                                    Logout
                                </button>
                            </>
                        ) : (
                            <div>
                                <Link href="/login" className="profile_fo_button profile_fo_button-primary">Login</Link>
                                <Link href="/registration" className="profile_fo_button profile_fo_button-secondary">Register</Link>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <main className="profile_fo_main">
                {/* Filters Section */}
                <div className="profile_fo_filters">
                    <div className="profile_fo_filters-header">
                        <h3 className="profile_fo_filters-title">
                            Filters / ફિલ્ટર્સ
                        </h3>
                        <div className="profile_fo_total-count">
                            <span className="profile_fo_total-label">Total:</span>
                            <span className="profile_fo_total-number">{filteredUsers.length}</span>
                            <span className="profile_fo_total-text">users</span>
                        </div>

                    </div>

                    <div className="profile_fo_filter-group">
                        {/* Search Filter */}
                        <div className="profile_fo_filter-category">
                            <label className="profile_fo_filter-label">Search / શોધો:</label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                <div style={{ position: 'relative', flex: 1 }}>
                                    <input
                                        type="text"
                                        placeholder={`Search across ALL profile fields (min. ${MIN_SEARCH_LENGTH} characters): name, profession, company, address, email, phone, social media, education, family info, and more...`}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        className={`profile_fo_search-input ${searchError ? 'profile_fo_search-error' : ''}`}
                                        style={{
                                            width: '100%',
                                            padding: '10px 40px 10px 12px',
                                            borderRadius: '6px',
                                            border: searchError ? '1px solid #ff6b6b' : '1px solid #ddd',
                                            fontSize: '14px',
                                            fontFamily: 'inherit',
                                            transition: 'border-color 0.3s'
                                        }}
                                    />
                                    {searchQuery && (
                                        <button
                                            onClick={() => setSearchQuery('')}
                                            style={{
                                                position: 'absolute',
                                                right: '8px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                background: 'none',
                                                border: 'none',
                                                color: '#666',
                                                cursor: 'pointer',
                                                fontSize: '16px',
                                                padding: '4px',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '24px',
                                                height: '24px',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                            title="Clear search"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                                <button
                                    onClick={executeSearch}
                                    disabled={searchLoading}
                                    style={{
                                        padding: '10px 16px',
                                        borderRadius: '6px',
                                        border: '1px solid #007bff',
                                        backgroundColor: searchLoading ? '#f8f9fa' : '#007bff',
                                        color: searchLoading ? '#6c757d' : 'white',
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        cursor: searchLoading ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        whiteSpace: 'nowrap'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!searchLoading) {
                                            e.target.style.backgroundColor = '#0056b3';
                                            e.target.style.borderColor = '#0056b3';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!searchLoading) {
                                            e.target.style.backgroundColor = '#007bff';
                                            e.target.style.borderColor = '#007bff';
                                        }
                                    }}
                                    title="Click to search or press Enter"
                                >
                                    {searchLoading ? (
                                        <>
                                            <span style={{ fontSize: '14px' }}>⏳</span>
                                            Searching...
                                        </>
                                    ) : (
                                        <>
                                            🔍 Search
                                        </>
                                    )}
                                </button>
                            </div>
                            {searchError && (
                                <div className="profile_fo_search-error-message" style={{
                                    color: '#ff6b6b',
                                    fontSize: '12px',
                                    marginTop: '4px',
                                    fontWeight: '500'
                                }}>
                                    {searchError}
                                </div>
                            )}
                            {activeSearchQuery && activeSearchQuery.length >= MIN_SEARCH_LENGTH && !searchLoading && !searchError && (
                                <div className="profile_fo_search-results-info" style={{
                                    color: '#666',
                                    fontSize: '12px',
                                    marginTop: '4px',
                                    fontStyle: 'italic'
                                }}>
                                    Found {filteredUsers.length} result{filteredUsers.length !== 1 ? 's' : ''} for "{activeSearchQuery}"
                                </div>
                            )}
                        </div>

                        {/* Division Filter */}
                        <div className="profile_fo_filter-category">
                            <label className="profile_fo_filter-label">વર્ગ:</label>
                            <div className="profile_fo_filter-options">
                                <button
                                    onClick={() => setSelectedDivision('')}
                                    className={`profile_fo_filter-option ${selectedDivision === '' ? 'profile_fo_filter-active' : ''}`}
                                >
                                    All / બધા
                                </button>
                                {['A', 'B', 'C'].map((division) => (
                                    <button
                                        key={division}
                                        onClick={() => setSelectedDivision(division)}
                                        className={`profile_fo_filter-option ${selectedDivision === division ? 'profile_fo_filter-active' : ''}`}
                                    >
                                        {division === 'A' ? 'અ' : division === 'B' ? 'બ' : 'ક'}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Role Filter */}
                        {/* <div className="profile_fo_filter-category">
                            <label className="profile_fo_filter-label">Role:</label>
                            <div className="profile_fo_filter-options">
                                <button
                                    onClick={() => setSelectedRole('')}
                                    className={`profile_fo_filter-option ${selectedRole === '' ? 'profile_fo_filter-active' : ''}`}
                                >
                                    All
                                </button>
                                <button
                                    onClick={() => setSelectedRole('teacher')}
                                    className={`profile_fo_filter-option ${selectedRole === 'teacher' ? 'profile_fo_filter-active' : ''}`}
                                >
                                    શિક્ષક / Teachers
                                </button>
                                <button
                                    onClick={() => setSelectedRole('student')}
                                    className={`profile_fo_filter-option ${selectedRole === 'student' ? 'profile_fo_filter-active' : ''}`}
                                >
                                    વિદ્યાર્થી / Students
                                </button>
                            </div>
                        </div> */}

                        {/* NRI Filter */}
                        <div className="profile_fo_filter-category shorting_btn_flex">
                            <div className="profile_fo_checkbox-container">
                                <input
                                    type="checkbox"
                                    id="nri-filter"
                                    checked={showNRI}
                                    onChange={(e) => setShowNRI(e.target.checked)}
                                    className="profile_fo_checkbox"
                                />
                                <label htmlFor="nri-filter" className="profile_fo_checkbox-label">
                                    <span className="profile_fo_checkbox-custom"></span>
                                    NRI Only
                                </label>
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 12 }}>
                                <button
                                    onClick={() => setSortOrder(prev => prev === 'alphabetical' ? 'latest' : 'alphabetical')}
                                    className={`profile_fo_sort-btn ${sortOrder === 'alphabetical' ? 'active' : ''}`}
                                    title={sortOrder === 'alphabetical' ? 'Showing alphabetical by name — click to show newest' : 'Showing newest — click to show alphabetical by name'}
                                    aria-pressed={sortOrder === 'alphabetical'}
                                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}
                                >
                                    {sortOrder === 'alphabetical' ? <FiArrowDown size={18} /> : <FiArrowUp size={18} />}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Users Grid */}
                <div className="profile_fo_profiles-grid">
                    {filteredUsers.map((user) => (
                        <Link
                            href={`/profile/${user._id}`}
                            key={user._id}
                            className="profile_fo_profile-card"
                            onClick={() => {
                                // Save current scroll position and flag
                                const currentScroll = window.scrollY;
                                sessionStorage.setItem('profilesScrollPosition', currentScroll.toString());
                                sessionStorage.setItem('comingFromProfiles', 'true');
                                sessionStorage.setItem('navigatingToProfile', 'true');
                            }}
                        >
                            <div className="profile_fo_profile-avatar">
                                {user.photo ? (
                                    <Image
                                        src={user.photo}
                                        alt={user.name}
                                        width={300}
                                        height={300}
                                        quality={100}
                                        loading="lazy"
                                        className="profile_fo_profile-image"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                ) : (
                                    <div className="profile_fo_profile-initial">
                                        {user.name?.charAt(0).toUpperCase()}
                                    </div>
                                )}
                                {user.isNRI && (
                                    <span className="profile_fo_nri-badge">NRI</span>
                                )}
                            </div>
                            <div className="profile_fo_profile-info">
                                <h3 className="profile_fo_profile-name">
                                    {user.name} {user.surname}
                                </h3>
                                <p className="profile_fo_profile-profession">
                                    {user.profession || 'Not specified'}
                                </p>
                                {user.division && (
                                    <div className="profile_fo_profile-division">
                                        <span className="profile_fo_division-badge">
                                            વર્ગ {user.division}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </Link>
                    ))}
                </div>

                {filteredUsers.length === 0 && (
                    <div className="profile_fo_empty-state">
                        <div className="profile_fo_empty-icon">👥</div>
                        <h3 className="profile_fo_empty-title">No users found</h3>
                        <p className="profile_fo_empty-message">
                            Try adjusting your filters to find what you're looking for
                        </p>
                    </div>
                )}



                <div className="fix_abcd_scroll">
                    <div className="fix_abcd">
                        <button
                            onClick={() => setSelectedLetter('')}
                            className={selectedLetter === '' ? 'active' : ''}
                        >
                            All
                        </button>
                        {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'].map((letter) => (
                            <button
                                key={letter}
                                onClick={() => setSelectedLetter(letter)}
                                className={selectedLetter === letter ? 'active' : ''}
                            >
                                {letter}
                            </button>
                        ))}
                    </div>
                </div>
            </main>

            <footer className="profile_fo_footer">
                <p className="profile_fo_footer-text">
                    &copy; આયોજન સમિતિ (SSC બેચ ૨૦૦૧)
                </p>
            </footer>
        </div>
    );
}